/*
 * Quantity steppers: the rolling number and the on-brand limit notice.
 * Also every price, total and count that goes up or down (see "prices,
 * totals and counts" below).
 * Shared by the drawer (Dawn's quantity-input), the cart page (.hlc-step,
 * a button group around a span) and the product page (.hl1a-qty).
 *
 *   Roll     when the number changes it rolls: up when it goes up (the new
 *            number comes from below), down when it goes down. A critically
 *            damped curve, no overshoot. A tap during a roll finishes the
 *            old one and starts from the number now showing, so fast taps
 *            never stack. Reduced motion just swaps the number.
 *   Respond  on the cart page the line only updates once the cart answers,
 *            so the number rolls the moment it's tapped and rolls back if
 *            the cart turns the change down.
 *   Notice   a limit the cart enforces (more than are in stock, say) shows
 *            as a small brown pill under the stepper, not the browser's
 *            validation bubble. window.hlQtyNotice(el, message), used by
 *            assets/cart.js.
 */
(function () {
  'use strict';

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  var SETTLE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  var STEPPER = '.hlc-step, .hl1a-qty';
  var running = typeof WeakMap === 'function' ? new WeakMap() : null;

  function valueEl(stepper) {
    return stepper && stepper.querySelector('input.hlc-step__val, span.hlc-step__val, .hl1a-qty__input');
  }

  function read(el) {
    return parseInt(el.tagName === 'INPUT' ? el.value : el.textContent, 10);
  }

  /* ---------- the roll ---------- */

  function roll(el, from, to) {
    if (!el || isNaN(from) || isNaN(to) || from === to) return;
    if (running) {
      var prev = running.get(el);
      if (prev) prev.finish();
    }
    if (reduceMotion || !canAnimate) {
      if (el.tagName !== 'INPUT') el.textContent = to;
      return;
    }
    var up = to > from;
    var isInput = el.tagName === 'INPUT';
    var h = el.offsetHeight || 20;
    var cs = window.getComputedStyle(el);

    // A window over the number, the same size and type, holding a two-number reel.
    var win = document.createElement('span');
    win.className = 'hl-qty-roll';
    win.setAttribute('aria-hidden', 'true');
    win.style.height = h + 'px';
    // One by one: the font shorthand often reads back empty for an input.
    ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'color'].forEach(function (k) {
      win.style[k] = cs[k];
    });
    var reel = document.createElement('span');
    reel.className = 'hl-qty-roll__reel';
    reel.style.lineHeight = h + 'px';
    var a = document.createElement('span');
    var b = document.createElement('span');
    a.textContent = up ? from : to;
    b.textContent = up ? to : from;
    reel.appendChild(a);
    reel.appendChild(b);
    win.appendChild(reel);

    var restore;
    if (isInput) {
      var host = el.parentNode;
      if (window.getComputedStyle(host).position === 'static') host.style.position = 'relative';
      win.style.position = 'absolute';
      win.style.left = el.offsetLeft + 'px';
      win.style.top = el.offsetTop + 'px';
      win.style.width = el.offsetWidth + 'px';
      host.appendChild(win);
      var colour = el.style.color;
      el.style.color = 'transparent';
      restore = function () {
        el.style.color = colour;
        if (win.parentNode) win.parentNode.removeChild(win);
      };
    } else {
      el.textContent = '';
      el.appendChild(win);
      restore = function () {
        el.textContent = to;
      };
    }

    var anim = reel.animate(
      up
        ? [{ transform: 'translateY(0)' }, { transform: 'translateY(-' + h + 'px)' }]
        : [{ transform: 'translateY(-' + h + 'px)' }, { transform: 'translateY(0)' }],
      { duration: 340, easing: SETTLE, fill: 'forwards' }
    );
    if (running) running.set(el, anim);
    var done = function () {
      if (running && running.get(el) === anim) running.delete(el);
      restore();
    };
    anim.finished.then(done, done);
  }

  /* ---------- watching the steppers ---------- */

  // Before any stepper's own handler runs, note the number; once they have
  // run, roll to whatever it became.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('.hlc-step__btn, .hl1a-qty__btn');
    var stepper = btn && btn.closest(STEPPER);
    var el = valueEl(stepper);
    if (!el || btn.disabled) return;
    var from = read(el);

    if (el.tagName !== 'INPUT') {
      // Cart page: the line only changes once the cart answers. Roll now,
      // unless the cart is still answering the last tap (it ignores this one).
      var page = btn.closest('hl-cart');
      if (page && page.classList.contains('is-busy')) return;
      var delta = parseInt(btn.getAttribute('data-hlc-qty'), 10);
      if (isNaN(delta)) return;
      var to = Math.max(1, from + delta);
      if (to === from) return;
      roll(el, from, to);
      watchForRefusal(btn.closest('[data-hlc-line]'), el, from, to, ahead(stepper, from, to));
      return;
    }
    setTimeout(function () {
      var to = read(el);
      roll(el, from, to);
      watchDrawerError(stepper, ahead(stepper, from, to));
    }, 0);
  }, true);

  // Typed straight into a box: roll from what it was when focused.
  document.addEventListener('focusin', function (e) {
    var el = e.target;
    if (el && el.matches && el.matches('input.hlc-step__val, .hl1a-qty__input')) el._hlQtyFrom = read(el);
  });
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.matches || !el.matches('input.hlc-step__val, .hl1a-qty__input')) return;
    if (typeof el._hlQtyFrom === 'number' && document.activeElement === el) {
      var stepper = el.closest(STEPPER);
      roll(el, el._hlQtyFrom, read(el));
      watchDrawerError(stepper, ahead(stepper, el._hlQtyFrom, read(el)));
      el._hlQtyFrom = read(el);
    }
  });

  // If the cart turns a cart-page change down, its row shows an error and
  // keeps its old line: roll the number back.
  function watchForRefusal(row, el, from, to, undo) {
    var err = row && row.querySelector('[data-hlc-line-error]');
    if (!err || !('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () {
      if (!err.hidden && el.isConnected && read(el) === to) {
        obs.disconnect();
        roll(el, to, from);
        if (undo) undo();
      }
    });
    obs.observe(err, { attributes: true, attributeFilter: ['hidden'] });
    // The row is replaced when the cart answers yes; stop watching then.
    setTimeout(function () { obs.disconnect(); }, 8000);
  }

  // The drawer (Dawn's cart) shows a refusal as text in the row's error.
  function watchDrawerError(stepper, undo) {
    var row = undo && stepper && stepper.closest('[data-key]');
    var err = row && row.querySelector('.cart-item__error-text');
    if (!err || !('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () {
      if (err.isConnected && err.textContent.trim()) {
        obs.disconnect();
        undo();
      }
    });
    obs.observe(err, { childList: true, characterData: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 8000);
  }

  /* ---------- the notice ---------- */

  var notice = null;
  var noticeTimer = null;

  function hideNotice() {
    clearTimeout(noticeTimer);
    if (!notice) return;
    var n = notice;
    notice = null;
    if (canAnimate && !reduceMotion) {
      n.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-4px) scale(0.97)' }], { duration: 160, easing: 'ease-out', fill: 'forwards' })
        .finished.then(function () { n.remove(); }, function () { n.remove(); });
    } else {
      n.remove();
    }
  }

  window.hlQtyNotice = function (el, message) {
    if (!message) return;
    hideNotice();
    var anchor = (el && el.closest && el.closest(STEPPER)) || el;
    if (!anchor) return;
    var r = anchor.getBoundingClientRect();
    var n = document.createElement('div');
    n.className = 'hl-qty-notice';
    n.setAttribute('role', 'status');
    // Dawn's strings carry placeholders for numbers it couldn't fill.
    n.textContent = String(message).replace(/\s*\[(min|max|step)\]/g, '').trim();
    document.body.appendChild(n);
    var w = n.offsetWidth;
    var left = Math.min(Math.max(12, r.left + r.width / 2 - w / 2), window.innerWidth - w - 12);
    n.style.left = left + 'px';
    n.style.top = r.bottom + 8 + 'px';
    // Grows out of the stepper it belongs to.
    n.style.transformOrigin = r.left + r.width / 2 - left + 'px top';
    if (canAnimate && !reduceMotion) {
      n.animate([{ opacity: 0, transform: 'translateY(-4px) scale(0.96)' }, { opacity: 1, transform: 'none' }], { duration: 240, easing: SETTLE });
    }
    notice = n;
    noticeTimer = setTimeout(hideNotice, 3200);
  };

  /* ---------- prices, totals and counts ---------- */

  /*
   * Any of these that changes from one number to another rolls, digit by
   * digit, odometer-style: $1,899 -> $3,798 rolls up, back down when it
   * drops. Only the characters that changed move; the rest stay put. A
   * change to the words (Add to cart -> Adding...) just swaps.
   *
   * Most of these are re-rendered from scratch (the drawer, the cart page
   * after each change), so the old value is remembered by where the number
   * sits, not by its element. A roll caught by a newer change jumps to it.
   */
  var ROLLS = [
    '[data-hl-roll]',
    '[data-hlc-total]',
    '[data-hlc-afterpay-amount]',
    '[data-hl-pdp-now]',
    '[data-hl-pdp-afterpay] strong',
    '[data-hl-pdp-add]',
    '[data-hl-room-total]',
    '.hl-header__cart-count'
  ];
  var memo = {};

  function num(s) {
    return parseFloat(String(s).replace(/[^\d.]/g, ''));
  }

  function skeleton(s) {
    return String(s).replace(/[\d,.]/g, '');
  }

  // Where each rollable number sits: its selector, its name, and which one
  // of those it is on the page.
  function eachRollable(fn) {
    ROLLS.forEach(function (sel, si) {
      var seen = {};
      Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
        if (el.closest('.hl-roll')) return;
        var name = el.getAttribute('data-hl-roll') || '';
        seen[name] = (seen[name] || 0) + 1;
        fn(el, si + '|' + name + '|' + seen[name]);
      });
    });
  }

  function shown(el) {
    var r = el._hlRoll;
    if (r && r.wrap.parentNode === el) return r.to;
    if (r) {
      // Someone wrote over a roll in progress: theirs is the number now.
      el._hlRoll = null;
      r.anims.forEach(function (a) { a.cancel(); });
    }
    return el.textContent.trim();
  }

  function stopText(el) {
    var r = el._hlRoll;
    if (!r) return;
    el._hlRoll = null;
    r.anims.forEach(function (a) { a.cancel(); });
    if (r.wrap.parentNode === el) el.textContent = r.to;
  }

  function rollText(el, from, to) {
    stopText(el);
    if (from === to || reduceMotion || !canAnimate || !el.offsetParent) return false;
    if (skeleton(from) !== skeleton(to) || isNaN(num(from)) || isNaN(num(to)) || num(from) === num(to)) return false;
    var up = num(to) > num(from);
    var cs = window.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (isNaN(lh)) {
      // "normal": measure what the font actually gives a line.
      var probe = document.createElement('span');
      probe.textContent = '0';
      el.appendChild(probe);
      lh = probe.getBoundingClientRect().height || parseFloat(cs.fontSize) * 1.2;
      el.removeChild(probe);
    }

    var wrap = document.createElement('span');
    wrap.className = 'hl-roll';
    wrap.setAttribute('aria-hidden', 'true');
    var said = document.createElement('span');
    said.className = 'visually-hidden';
    said.textContent = to;

    // The words stay put (the skeletons match); each number lines up with
    // its old self from the right, units under units.
    var NUM = /[\d.,]+/g;
    var oldNums = from.match(NUM) || [];
    var parts = to.split(/([\d.,]+)/);
    var cols = [];
    var ni = 0;
    parts.forEach(function (part, pi) {
      if (pi % 2 === 0) {
        if (part) wrap.appendChild(document.createTextNode(part));
        return;
      }
      var was = oldNums[ni++] || '';
      var pad = part.length - was.length;
      for (var i = 0; i < part.length; i++) {
        var b = part.charAt(i);
        var a = i - pad >= 0 ? was.charAt(i - pad) : '';
        if (a === b) {
          wrap.appendChild(document.createTextNode(b));
          continue;
        }
        var col = document.createElement('span');
        col.className = 'hl-roll__col';
        col.style.lineHeight = lh + 'px';
        var sizer = document.createElement('span');
        sizer.className = 'hl-roll__sizer';
        sizer.textContent = b;
        var reel = document.createElement('span');
        reel.className = 'hl-roll__reel';
        var top = document.createElement('span');
        var bottom = document.createElement('span');
        top.textContent = (up ? a : b) || '\u00a0';
        bottom.textContent = (up ? b : a) || '\u00a0';
        top.style.height = bottom.style.height = lh + 'px';
        reel.appendChild(top);
        reel.appendChild(bottom);
        col.appendChild(sizer);
        col.appendChild(reel);
        wrap.appendChild(col);
        cols.push(reel);
      }
    });
    if (!cols.length) return false;

    el.textContent = '';
    el.appendChild(wrap);
    el.appendChild(said);
    var r = { to: to, wrap: wrap, anims: [] };
    el._hlRoll = r;
    // The units go first and each digit to the left a beat later, like an
    // odometer turning over.
    cols.forEach(function (reel, k) {
      var delay = Math.min((cols.length - 1 - k) * 30, 150);
      r.anims.push(reel.animate(
        up
          ? [{ transform: 'translateY(0)' }, { transform: 'translateY(-' + lh + 'px)' }]
          : [{ transform: 'translateY(-' + lh + 'px)' }, { transform: 'translateY(0)' }],
        { duration: 420, delay: delay, easing: SETTLE, fill: 'both' }
      ));
    });
    Promise.all(r.anims.map(function (x) { return x.finished; })).then(function () {
      if (el._hlRoll !== r) return;
      el._hlRoll = null;
      if (wrap.parentNode === el) el.textContent = to;
    }, function () {});
    return true;
  }

  function scanRolls() {
    eachRollable(function (el, key) {
      var cur = shown(el);
      var was = memo[key];
      memo[key] = cur;
      if (was === undefined || was === cur) return;
      // Leave anything holding more than text (an icon, say) as it is.
      if (el.children.length) return;
      rollText(el, was, cur);
    });
  }

  // For a caller that knows the old number better than the page does (the
  // set swap's badge after the cart page reloads).
  window.hlRollText = function (el, from, to) {
    from = String(from);
    to = String(to);
    if (!rollText(el, from, to)) stopText(el);
    eachRollable(function (x, key) {
      if (x === el) memo[key] = to;
    });
  };

  /*
   * Money, ahead of the cart. The cart page and the drawer only re-render
   * once Shopify answers (the drawer also waits out Dawn's debounce), so a
   * tap on + or - rolls the line price, subtotal, total and Afterpay amount
   * straight away, worked out from the line's unit price. When the cart
   * answers with the same numbers nothing moves again; if it differs (a
   * discount, say) they roll on to its numbers, and if it says no they roll
   * back. Returns that roll-back, or null when there's nothing to work from.
   */
  function cents(text) {
    var n = parseFloat(String(text).replace(/[^\d.]/g, ''));
    return isNaN(n) ? null : Math.round(n * 100);
  }

  function dollars(c, always2) {
    var whole = c % 100 === 0 && !always2;
    return '$' + (c / 100).toLocaleString('en-AU', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  }

  function setNumber(el, text, undo) {
    var was = shown(el);
    if (was === text) return;
    undo.push([el, was]);
    if (!rollText(el, was, text)) el.textContent = text;
    eachRollable(function (x, key) {
      if (x === el) memo[key] = text;
    });
  }

  function ahead(stepper, from, to) {
    var row = stepper && stepper.closest('[data-key]');
    var scope = stepper && stepper.closest('hl-cart, cart-drawer');
    if (!row || !scope || isNaN(from) || isNaN(to) || from < 1 || to < 1 || from === to) return null;
    var line = row.querySelector('[data-hl-roll^="line-"]');
    var lineNow = line && cents(shown(line));
    if (!lineNow) return null;
    var delta = Math.round((lineNow / from) * (to - from));
    var undo = [];
    var now = cents(shown(line));
    setNumber(line, dollars(Math.max(0, now + delta)), undo);
    return shiftTotals(scope, delta, undo);
  }

  // Move the subtotal, total and Afterpay amount in a cart by delta cents.
  // Returns the roll-back.
  function shiftTotals(scope, delta, undo) {
    var bump = function (el) {
      var now = el && cents(shown(el));
      if (now === null || now === undefined) return null;
      setNumber(el, dollars(Math.max(0, now + delta)), undo);
      return now + delta;
    };
    var sub = bump(scope.querySelector('[data-hl-roll="subtotal"]'));
    var total = bump(scope.querySelector('[data-hlc-total]'));
    var base = total !== null && total !== undefined ? total : sub;
    var ap = scope.querySelector('[data-hlc-afterpay-amount]');
    if (ap && base !== null && base !== undefined) setNumber(ap, dollars(Math.floor(Math.max(0, base) / 4), true), undo);
    return function () {
      undo.reverse().forEach(function (u) {
        if (u[0].isConnected) setNumber(u[0], u[1], []);
      });
    };
  }

  // A line leaving the cart (assets/hl-cart-undo.js): its price comes off
  // the totals straight away.
  window.hlCartLineGone = function (row) {
    var scope = row && row.closest('hl-cart, cart-drawer');
    var line = row && row.querySelector('[data-hl-roll^="line-"]');
    var price = line && cents(shown(line));
    if (!scope || !price) return null;
    return shiftTotals(scope, -price, []);
  };

  var rollQueued = false;
  function queueRolls() {
    if (rollQueued) return;
    rollQueued = true;
    requestAnimationFrame(function () {
      rollQueued = false;
      scanRolls();
    });
  }

  function startRolls() {
    scanRolls();
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        var host = t.nodeType === 1 ? t : t.parentNode;
        // Our own reels moving in and out don't count.
        if (host && host.closest && host.closest('.hl-roll')) continue;
        queueRolls();
        return;
      }
    }).observe(document.body, { childList: true, characterData: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startRolls);
  } else {
    startRolls();
  }

  ['scroll', 'resize'].forEach(function (ev) {
    window.addEventListener(ev, hideNotice, { passive: true, capture: true });
  });
  document.addEventListener('pointerdown', function (e) {
    if (notice && !notice.contains(e.target)) hideNotice();
  }, true);
})();
